import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'

import {
  buildCheckpoints,
  buildReassignmentLog,
  formatMs,
  groupTurns,
  labelFor,
  type ReassignmentFold,
  shownAt,
  visibleLines,
} from './liveReplay'
import { hasTokenText, RecordedActivity } from './RecordedActivity'

import styles from './ReplayMode.module.scss'

import { isApiError } from '@/api'
import { type LiveReplayLine, type LiveReplayResponse, replayMeetingLiveSpeakers } from '@/api/internal'
import Button from '@/components/Button'
import { Spinner } from '@/components/Spinner'
import { parseTranscriptNdjson } from '@/pages/app/AttributionViewer/artifactDerivations'
import { liveArtifactQueryOptions } from '@/pages/app/AttributionViewer/artifactQueries'
import {
  describeLiveProvider,
  type LiveProviderProvenance,
  parseLiveProvider,
  summarizeLiveProviders,
} from '@/pages/app/AttributionViewer/liveProvider'
import { attributionViewerKeys } from '@/pages/app/AttributionViewer/queryKeys'

const errorMessage = (error: unknown): string => {
  const status = isApiError(error) ? error.status : undefined
  if (status === 404) return 'No meeting with this ID.'
  if (status === 413) return "This meeting's evidence is too large to replay."
  if (status === 429 || status === 503) return 'Every replay slot is busy. Try again in a minute.'
  if (status === 504) return 'The replay took too long and stopped.'
  return "Couldn't replay this meeting."
}

export const ReplayMode = ({ meetingId }: { meetingId: string }) => {
  const query = useQuery({
    queryKey: attributionViewerKeys.liveReplay(meetingId),
    queryFn: ({ signal }) => replayMeetingLiveSpeakers(meetingId, signal).then((r) => r.data),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  })
  // The replay folds the same live NDJSON, so its entry ids key the provider each line was stamped with.
  const providersQuery = useQuery({
    ...liveArtifactQueryOptions(meetingId),
    select: readLiveProviders,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  if (query.isPending) {
    return (
      <div className={styles.status} role="status">
        <Spinner size={24} color="currentColor" />
        <span>Replaying the meeting…</span>
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className={styles.status} role="alert">
        <span>{errorMessage(query.error)}</span>
        <Button title="Try again" type="light" onClick={() => void query.refetch()} />
      </div>
    )
  }
  return <ReplayView key={meetingId} replay={query.data} providers={providersQuery.data ?? null} />
}

type LineProvider = { provenance: LiveProviderProvenance | undefined; detail: string }
type LiveProviders = ReadonlyMap<string, LineProvider>

const unknownProvider: LineProvider = { provenance: undefined, detail: describeLiveProvider(undefined) }

const readLiveProviders = (body: string): LiveProviders => {
  const byEntry = new Map<string, LineProvider>()
  for (const entry of parseTranscriptNdjson(body).entries) {
    if (!('speaker' in entry)) continue
    const provenance = parseLiveProvider(entry.liveProvider)
    byEntry.set(entry.id, { provenance, detail: describeLiveProvider(provenance) })
  }
  return byEntry
}

// A turn can span a provider switch, so it lists every provider its lines came from.
const turnProviders = (lines: readonly LiveReplayLine[], providerOf: (line: LiveReplayLine) => LineProvider) =>
  Array.from(new Set(lines.map((line) => providerOf(line).detail))).join(' / ')

const turnSpeakerIds = (lines: readonly LiveReplayLine[]) => Array.from(new Set(lines.map((line) => line.speaker_id)))

const ReplayView = ({ replay, providers }: { replay: LiveReplayResponse; providers: LiveProviders | null }) => {
  const { folds, lines } = replay
  const [index, setIndex] = useState(folds.length > 0 ? 0 : -1)
  const [jumps, setJumps] = useState(0)
  const checkpoints = useMemo(() => buildCheckpoints(folds), [folds])
  const reassignments = useMemo(() => buildReassignmentLog(folds, lines), [folds, lines])
  const transcriptRef = useRef<HTMLOListElement>(null)

  const atMs = index >= 0 ? folds[index].at_ms : 0
  const shown = useMemo(() => shownAt(folds, checkpoints, index), [folds, checkpoints, index])
  const previous = useMemo(() => shownAt(folds, checkpoints, index - 1), [folds, checkpoints, index])
  const visible = useMemo(() => visibleLines(lines, atMs), [lines, atMs])
  const previousAtMs = index > 0 ? folds[index - 1].at_ms : -1

  const rows = visible.map((line) => {
    const label = labelFor(line, shown)
    const wasShown = line.appear_ms <= previousAtMs
    const before = wasShown ? labelFor(line, previous) : null
    return { line, label, before: before !== null && before !== label ? before : null }
  })
  const relabeled = rows.filter((row) => row.before !== null).length
  const rowsById = new Map(rows.map((row) => [row.line.entry_id, row]))
  const turns = groupTurns(visible, shown)
  // The replay drops refined and untimed live rows, so the summary covers only the lines it replays.
  const providerSummary = useMemo(() => {
    if (!providers) return null
    const providerOf = (line: LiveReplayLine) => providers.get(line.entry_id) ?? unknownProvider
    const lineProviders = lines.map(providerOf)
    return {
      providerOf,
      label: summarizeLiveProviders(lineProviders.map((provider) => provider.provenance)),
      details: Array.from(new Set(lineProviders.map((provider) => provider.detail))),
    }
  }, [lines, providers])
  const turnProviderOf = providerSummary && providerSummary.details.length > 1 ? providerSummary.providerOf : null

  const lastIndexRef = useRef(index)
  useEffect(() => {
    const el = transcriptRef.current
    const forward = index > lastIndexRef.current
    lastIndexRef.current = index
    if (!el) return
    const changed = el.querySelector<HTMLElement>('[data-changed]')
    if (changed) changed.scrollIntoView({ block: 'nearest' })
    else if (forward) el.scrollTop = el.scrollHeight
  }, [index, jumps])

  return (
    <div className={styles.replay}>
      <div className={styles.main}>
        <div className={styles.titleRow}>
          <h3 className={styles.sectionTitle}>Estimated transcript</h3>
          {providerSummary ? (
            <span className={styles.provider} title={providerSummary.details.join('\n')}>
              {providerSummary.label}
            </span>
          ) : null}
        </div>
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.step}
            onClick={() => setIndex((i) => i - 1)}
            disabled={index <= 0}
            aria-label="Previous fold"
          >
            <ChevronLeft size={16} />
          </button>
          <input
            type="range"
            className={styles.scrubber}
            min={0}
            max={Math.max(folds.length - 1, 0)}
            value={Math.max(index, 0)}
            onChange={(e) => setIndex(Number(e.target.value))}
            disabled={folds.length < 2}
            aria-label="Fold"
            aria-valuetext={`Fold ${index + 1} of ${folds.length}, at ${formatMs(atMs)}`}
          />
          <button
            type="button"
            className={styles.step}
            onClick={() => setIndex((i) => i + 1)}
            disabled={index >= folds.length - 1}
            aria-label="Next fold"
          >
            <ChevronRight size={16} />
          </button>
          <span className={styles.position}>
            {folds.length > 0 ? `Fold ${index + 1} of ${folds.length} · ${formatMs(atMs)}` : 'No folds'}
          </span>
        </div>
        <p className={styles.caption}>
          Lines arrive {replay.appear_lag_ms} ms after they end; folds run every {replay.batch_ms / 1000} s.
          {relabeled > 0 ? ` This fold relabeled ${relabeled} ${relabeled === 1 ? 'line' : 'lines'}.` : ''}
        </p>
        {replay.truncated_at_ms != null ? (
          <p className={styles.notice}>
            The replay stopped at {formatMs(replay.truncated_at_ms)} because its output got too large.
          </p>
        ) : null}
        {!replay.has_live ? (
          <p className={styles.empty}>This meeting has no live transcript to replay.</p>
        ) : lines.length === 0 ? (
          <p className={styles.empty}>The live transcript has no timed lines to replay.</p>
        ) : (
          <ol className={styles.transcript} ref={transcriptRef} aria-label="Live transcript at this fold">
            {turns.map((turn) => {
              const speakerIds = turnSpeakerIds(turn.lines)
              const mixedSpeakers = speakerIds.length > 1
              return (
                <li key={turn.key} className={styles.line}>
                  <div className={styles.lineHeader}>
                    <span className={styles.speaker}>{turn.label}</span>
                    <span className={styles.source}>{turn.lines[0].source}</span>
                    <span className={styles.source}>
                      {mixedSpeakers ? 'Speakers' : 'Speaker'} {speakerIds.join(', ')}
                    </span>
                    {turnProviderOf ? (
                      <span className={styles.source}>{turnProviders(turn.lines, turnProviderOf)}</span>
                    ) : null}
                  </div>
                  {turn.lines.map((line) => {
                    const before = rowsById.get(line.entry_id)?.before ?? null
                    return (
                      <div
                        key={line.entry_id}
                        className={clsx(styles.turnLine, before !== null && styles.changed)}
                        data-changed={before !== null || undefined}
                      >
                        <span className={styles.time}>{formatMs(line.start_ms)}</span>
                        <p className={styles.text}>
                          {mixedSpeakers ? (
                            <>
                              <span className={styles.source}>Speaker {line.speaker_id}</span>{' '}
                            </>
                          ) : null}
                          {line.text}
                          {before !== null ? <span className={styles.was}>was {before}</span> : null}
                        </p>
                      </div>
                    )
                  })}
                </li>
              )
            })}
          </ol>
        )}
      </div>
      <aside className={styles.aside}>
        <ReassignmentLog
          log={reassignments}
          activeIndex={index}
          onSelect={(i) => {
            setIndex(i)
            setJumps((n) => n + 1)
          }}
        />
        <RecordedActivity timing={replay.timing} />
        <details className={styles.limits}>
          <summary className={styles.limitsSummary}>
            <ChevronDown size={14} className={styles.limitsChevron} aria-hidden />
            Transcript replay limits
          </summary>
          <ul className={styles.gaps}>
            <li>Transcript arrival times are estimates. Recorded activity uses a separate connection clock.</li>
            <li>Evidence events are deleted after the meeting, so the replay rebuilds them from the stored files.</li>
            {hasTokenText(replay.timing) ? (
              <li>
                Recorded activity replays desktop&apos;s merge one connection at a time, so mic and system rows are not
                interleaved.
              </li>
            ) : (
              <li>
                Partial word revisions were not stored. Recorded activity shows token timing and speakers without text.
              </li>
            )}
            <li>
              The replay uses the current algorithm ({replay.algorithm_version}), which may differ from the one that ran
              live.
            </li>
            <li>Renames and reassignments made on desktop during the meeting aren&apos;t included.</li>
            <li>After a pause, each part replays on its own; earlier parts keep their last labels.</li>
          </ul>
        </details>
        {replay.has_observations ? null : (
          <p className={styles.asideNote}>This meeting has no speaker observations, so only voices are labeled.</p>
        )}
        <dl className={styles.stats}>
          <dt>Events folded</dt>
          <dd>{replay.event_count}</dd>
          <dt>Segments</dt>
          <dd>{replay.segment_count}</dd>
        </dl>
      </aside>
    </div>
  )
}

const ReassignmentLog = ({
  log,
  activeIndex,
  onSelect,
}: {
  log: ReassignmentFold[]
  activeIndex: number
  onSelect: (index: number) => void
}) => (
  <section className={styles.log} aria-label="Speaker reassignments">
    <h3 className={styles.sectionTitle}>
      Speaker reassignments <span className={styles.count}>{log.length}</span>
    </h3>
    {log.length === 0 ? (
      <p className={styles.asideNote}>No shown line changed speaker during the replay.</p>
    ) : (
      <ol className={styles.logList}>
        {log.map((entry) => (
          <li key={entry.index}>
            <button
              type="button"
              className={clsx(styles.logEntry, entry.index === activeIndex && styles.logEntryActive)}
              onClick={() => onSelect(entry.index)}
              aria-current={entry.index === activeIndex || undefined}
            >
              <span className={styles.time}>{formatMs(entry.atMs)}</span>
              <span className={styles.logChanges}>
                {entry.changes.map((change) => (
                  <span key={`${change.from}→${change.to}`}>
                    <span className={styles.logMuted}>{change.from}</span> → {change.to}
                    <span className={styles.logMuted}>
                      {' '}
                      · {change.lines.length} {change.lines.length === 1 ? 'line' : 'lines'} from{' '}
                      {formatMs(change.lines[0].start_ms)}
                    </span>
                  </span>
                ))}
              </span>
            </button>
          </li>
        ))}
      </ol>
    )}
  </section>
)
